import { uploadBadgeTemplate, useCompaniesNames } from "@/api";
import PageBackButton from "@/components/PageBackButton";
import {
  Breadcrumbs,
  Button,
  FileInput,
  Icon,
  ImageInput,
  PopupCard,
} from "@app-components";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import styles from "@/styles/NewTemplate.module.scss";
import { useState } from "react";
import { TemplatePreview } from "@/components/badge_printing/TemplatePreview";
import {
  ExportImageData,
  NotificationType,
  TemplateFormType,
  UploadTemplateAssetsPayload,
} from "@/types";
import { useSnackbarStore } from "@/store/zustandStore";
import { TemplateNameField } from "@/components/badge_printing/TemplateNameField";
import { templateNameValidation } from "@/components/badge_printing/utils/templateNameValidation";
import { AssignedToCompaniesField } from "@/components/badge_printing/AssignedToCompanyField";
import { usePrompt } from "@/hooks/usePrompt";
import { useQueryClient } from "@tanstack/react-query";
import { getBuildingId } from "@/lib";
import { useBuildingAdminAccess } from "@/hooks/useBuildingAdminAccess";

export const Route = createFileRoute(
  "/_portal/building/settings/template/create",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: companies } = useCompaniesNames([], {
    getAllIfNoCompanyIds: true,
    noExternal: true,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { addNotification } = useSnackbarStore();
  useBuildingAdminAccess();

  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  const companyOptions = [
    ...companies.map((company) => ({
      label: company.buildingTenantName ?? "",
      value: company.buildingTenantId ?? "",
    })),
  ];

  const breadcrumbs = [
    {
      label: t("SIDEBAR.BUILDING_SETTINGS"),
      path: "/building/settings",
    },
    {
      label: "Welcome Access",
      path: `/building/settings/access`,
    },
    {
      label: t("BADGE_PRINTING.TEMPLATES.NEW_TEMPLATE"),
      path: `/building/settings/template/create`,
    },
  ];

  const form = useForm<TemplateFormType>({
    defaultValues: {
      templateName: "",
      assignedToCompanies: [],
      assignedToAllCompanies: false,
      frontHtml: null,
    },
    onSubmit: async (values) => {
      const isAll = values.value.assignedToCompanies.includes("__ALL__");

      const finalData = {
        ...values.value,
        assignedToCompanies: isAll ? [] : values.value.assignedToCompanies,
        assignedToAllCompanies: isAll,
      };

      if (finalData.frontHtml && finalData.frontHtml instanceof File) {
        const frontImageData =
          finalData.backgroundImageFront as ExportImageData | null;
        const backImageData =
          finalData.backgroundImageBack as ExportImageData | null;

        const frontImageBlob =
          frontImageData && "raw" in frontImageData
            ? frontImageData.raw
            : undefined;

        const backImageBlob =
          backImageData && "raw" in backImageData
            ? backImageData.raw
            : undefined;
        setUploadingTemplate(true);
        try {
          const templateId = await uploadTemplateAssets({
            frontHtmlFile: finalData.frontHtml,
            backHtmlFile: finalData.backHtml as File | undefined,
            cssFile: finalData.stylesCss as File | undefined,
            backgroundImageFrontFile: frontImageBlob,
            backgroundImageBackFile: backImageBlob,
            templateName: finalData.templateName,
            assignedToCompanies: finalData.assignedToCompanies,
            assignedToAllCompanies: finalData.assignedToAllCompanies,
          });
          addNotification(
            t("BADGE_PRINTING.TEMPLATE_FORM.SUCCESS"),
            NotificationType.SUCCESS,
          );
          navigate({
            to: "/building/settings/template/$templateId",
            params: { templateId: templateId! },
          });
          queryClient.invalidateQueries({
            queryKey: [getBuildingId(), "badge-templates"],
          });
          queryClient.invalidateQueries({
            queryKey: [getBuildingId(), "templates-for-company"],
          });
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error("Error uploading badge template:", error);
          addNotification(t("BADGE_PRINTING.TEMPLATE_FORM.ERROR"));
        }
        setUploadingTemplate(false);
      }
    },
  });

  const encodeFileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadTemplateAssets = async ({
    frontHtmlFile,
    backHtmlFile,
    cssFile,
    backgroundImageFrontFile,
    backgroundImageBackFile,
    templateName,
    assignedToCompanies = [],
    assignedToAllCompanies = false,
  }: {
    frontHtmlFile: File;
    backHtmlFile?: File;
    cssFile?: File;
    backgroundImageFrontFile?: Blob;
    backgroundImageBackFile?: Blob;
    templateName: string;
    assignedToCompanies: string[];
    assignedToAllCompanies: boolean;
  }): Promise<string | undefined> => {
    const payload: UploadTemplateAssetsPayload = {
      templateName,
      assignedToAllCompanies,
      assignedToCompanies,
      frontHtml: await encodeFileToBase64(frontHtmlFile),
      images: {},
    };

    if (frontHtmlFile instanceof File) {
      payload.frontHtml = await encodeFileToBase64(frontHtmlFile);
    }
    if (backHtmlFile && backHtmlFile instanceof File)
      payload.backHtml = await encodeFileToBase64(backHtmlFile);
    if (cssFile && cssFile instanceof File)
      payload.cssFile = await encodeFileToBase64(cssFile);

    if (backgroundImageFrontFile)
      payload.images.backgroundImageFront = await encodeFileToBase64(
        backgroundImageFrontFile,
      );
    if (backgroundImageBackFile)
      payload.images.backgroundImageBack = await encodeFileToBase64(
        backgroundImageBackFile,
      );

    return await uploadBadgeTemplate(payload);
  };

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/settings/access",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("BADGE_PRINTING.TEMPLATES.NEW_TEMPLATE")}</h1>
        <p>{t("BADGE_PRINTING.TEMPLATES.NEW_TEMPLATE_DESCRIPTION")}</p>
      </div>
      <form
        onChange={() => setHasUnsavedChanges(true)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field name="templateName" validators={templateNameValidation(t)}>
          {(field) => <TemplateNameField field={field} />}
        </form.Field>
        <form.Field
          name="assignedToCompanies"
          validators={{
            onChange: ({ value }) =>
              value.length
                ? ""
                : t("BADGE_PRINTING.TEMPLATES.ASSIGN_COMPANIES_REQUIRED"),
          }}
        >
          {(field) => (
            <AssignedToCompaniesField
              field={field}
              form={form}
              companyOptions={companyOptions}
            />
          )}
        </form.Field>

        <div className={styles.infoBox}>
          <p>
            <Icon name="info" />
            <strong>{t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_TITLE")}</strong>
          </p>
          <i>
            <ul>
              <li>{t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_NAME")}</li>
              <li>{t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_EMPLOYEE_ID")}</li>
              <li>{t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_JOB_TITLE")}</li>
              <li>
                {t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_PROFILE_PHOTO")}
              </li>
              <li>
                {t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_BACKGROUND_IMAGES")}
              </li>
              <li>{t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_COMPANY")}</li>
            </ul>
          </i>
          <p>{t("BADGE_PRINTING.TEMPLATE_FORM.HTML_INFO_NOTE")}</p>
        </div>
        <div className={styles.filesInputSection}>
          <div className={styles.htmlTemplatesContainer}>
            <form.Field name="frontHtml">
              {(field) => (
                <FileInput
                  label={t("BADGE_PRINTING.TEMPLATE_FORM.FRONT_HTML")}
                  onChange={(file) => field.setValue(file ?? null)}
                  accept=".html"
                  required
                  translations={{
                    fileUploadPlaceholder: t(
                      "BADGE_PRINTING.TEMPLATE_FORM.HTML",
                    ),
                    fileUploadOverlayPlaceholder: t(
                      "BADGE_PRINTING.TEMPLATE_FORM.HTML_PLACEHOLDER",
                    ),
                  }}
                />
              )}
            </form.Field>
            <form.Field name="backHtml">
              {(field) => (
                <FileInput
                  label={t("BADGE_PRINTING.TEMPLATE_FORM.BACK_HTML")}
                  onChange={(file) => field.setValue(file ?? null)}
                  accept=".html"
                  translations={{
                    fileUploadPlaceholder: t(
                      "BADGE_PRINTING.TEMPLATE_FORM.HTML",
                    ),
                    fileUploadOverlayPlaceholder: t(
                      "BADGE_PRINTING.TEMPLATE_FORM.HTML_PLACEHOLDER",
                    ),
                  }}
                />
              )}
            </form.Field>
          </div>

          <form.Field name="stylesCss">
            {(field) => (
              <FileInput
                label={t("BADGE_PRINTING.TEMPLATE_FORM.CSS")}
                onChange={(file) => field.setValue(file ?? null)}
                accept=".css"
                translations={{
                  fileUploadPlaceholder: t(
                    "BADGE_PRINTING.TEMPLATE_FORM.ADD_CSS",
                  ),
                  fileUploadOverlayPlaceholder: t(
                    "BADGE_PRINTING.TEMPLATE_FORM.CSS_PLACEHOLDER",
                  ),
                }}
              />
            )}
          </form.Field>

          <div className={styles.imagesInputContainer}>
            <form.Field name="backgroundImageFront">
              {(field) => (
                <div className={styles.imageInputWrapper}>
                  <label
                    htmlFor="backgroundImageFront"
                    className={styles.label}
                  >
                    {t("BADGE_PRINTING.TEMPLATE_FORM.BACKGROUND_IMAGE_FRONT")}
                  </label>
                  <ImageInput
                    id="backgroundImageFront"
                    strategy="cover"
                    multiple={false}
                    placeholder={
                      <div className={styles.imageUploadPlaceholder}>
                        <Icon name="add_photo" />
                        <p>{t("BADGE_PRINTING.TEMPLATE_FORM.ADD_IMAGE")}</p>
                      </div>
                    }
                    translations={{
                      uploadImageOverlayText: t("IMAGE.UPLOAD"),
                      replaceImageAriaLabel: t("IMAGE.REPLACE.ARIA_LABEL"),
                      replaceImageOverlayText: t("IMAGE.REPLACE.TEXT"),
                      undoChangeButtonText: t("IMAGE.UNDO"),
                      editDoneText: t("IMAGE.DONE"),
                      editCancelText: t("IMAGE.CANCEL"),
                      fileTooLargeText: t("IMAGE.FILE_TOO_LARGE"),
                      dropZoneText: t("IMAGE.DROP_ZONE_TEXT"),
                    }}
                    onError={(error, type) => {
                      console.error(`Error uploading ${type}:`, error);
                    }}
                    onChange={(data) => field.setValue(data ?? null)}
                    style={{
                      containerStyle: {
                        width: "100%",
                        height: "fit-content",
                        minHeight: 120,
                        backgroundColor: "var(--wlcm-color-inactive-grey)",
                      },
                      previewStyle: {
                        image: {
                          borderRadius: "var(--wlcm-border-radius-sm)",
                          width: "100%",
                          height: "100%",
                          maxHeight: 120,
                          maxWidth: 800,
                          objectFit: "cover",
                        },
                      },
                      overlayStyle: {
                        borderRadius: "var(--wlcm-border-radius-sm)",
                      },
                    }}
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="backgroundImageBack">
              {(field) => (
                <div className={styles.imageInputWrapper}>
                  <label htmlFor="backgroundImageBack" className={styles.label}>
                    {t("BADGE_PRINTING.TEMPLATE_FORM.BACKGROUND_IMAGE_BACK")}
                  </label>
                  <ImageInput
                    id="backgroundImageBack"
                    strategy="cover"
                    multiple={false}
                    placeholder={
                      <div className={styles.imageUploadPlaceholder}>
                        <Icon name="add_photo" />
                        <p>{t("BADGE_PRINTING.TEMPLATE_FORM.ADD_IMAGE")}</p>
                      </div>
                    }
                    translations={{
                      uploadImageOverlayText: t("IMAGE.UPLOAD"),
                      replaceImageAriaLabel: t("IMAGE.REPLACE.ARIA_LABEL"),
                      replaceImageOverlayText: t("IMAGE.REPLACE.TEXT"),
                      undoChangeButtonText: t("IMAGE.UNDO"),
                      editDoneText: t("IMAGE.DONE"),
                      editCancelText: t("IMAGE.CANCEL"),
                      fileTooLargeText: t("IMAGE.FILE_TOO_LARGE"),
                      dropZoneText: t("IMAGE.DROP_ZONE_TEXT"),
                    }}
                    onError={(error, type) => {
                      console.error(`Error uploading ${type}:`, error);
                    }}
                    onChange={(data) => field.setValue(data ?? null)}
                    style={{
                      containerStyle: {
                        width: "100%",
                        height: "fit-content",
                        minHeight: 120,
                        backgroundColor: "var(--wlcm-color-inactive-grey)",
                      },
                      previewStyle: {
                        image: {
                          borderRadius: "var(--wlcm-border-radius-sm)",
                          width: "100%",
                          height: "100%",
                          maxHeight: 120,
                          maxWidth: 800,
                          objectFit: "cover",
                        },
                      },
                      overlayStyle: {
                        borderRadius: "var(--wlcm-border-radius-sm)",
                      },
                    }}
                  />
                </div>
              )}
            </form.Field>
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <form.Subscribe selector={(form) => form.values.frontHtml}>
            {(frontHtml) => (
              <Button
                type="button"
                secondary
                onClick={() => setShowPreview(true)}
                disabled={
                  !frontHtml ||
                  !(frontHtml instanceof File) ||
                  uploadingTemplate
                }
              >
                {t("BADGE_PRINTING.TEMPLATE_FORM.PREVIEW")}
              </Button>
            )}
          </form.Subscribe>
          <Button
            type="submit"
            disabled={uploadingTemplate}
            isLoading={uploadingTemplate}
          >
            {t("BADGE_PRINTING.TEMPLATE_FORM.SAVE")}
          </Button>
        </div>
      </form>
      <PopupCard
        onClose={() => setShowPreview(false)}
        whiteBackground
        open={showPreview}
      >
        <div>
          <h3>{t("BADGE_PRINTING.TEMPLATE_FORM.TEMPLATE_PREVIEW")}</h3>
        </div>
        <form.Subscribe
          selector={(state) => ({
            frontHtml: state.values.frontHtml,
            backHtml: state.values.backHtml,
            stylesCss: state.values.stylesCss,
            backgroundImageFront: state.values.backgroundImageFront,
            backgroundImageBack: state.values.backgroundImageBack,
          })}
        >
          {({
            frontHtml,
            backHtml,
            stylesCss,
            backgroundImageFront,
            backgroundImageBack,
          }) => (
            <TemplatePreview
              frontHtml={frontHtml ?? null}
              backHtml={backHtml ?? null}
              cssFile={stylesCss ?? null}
              backgroundImageFront={backgroundImageFront?.previewUrl ?? null}
              backgroundImageBack={backgroundImageBack?.previewUrl ?? null}
              showInputFields={true}
            />
          )}
        </form.Subscribe>
      </PopupCard>
    </>
  );
}
